/**
 * §G AI-Suggest surfaces — THREE suggest pills that together cover every
 * editable §G field, split out of CompliancePanel.tsx for the file-size limit
 * (see that file's header). Still §G's typed CompliancePanel, just the suggest
 * wrappers extracted.
 *
 * SHARED CONTRACT. These pills call `requestModelFieldSuggestions` (the A–I
 * seam in `@/services/brandBibleAiSuggest`) which POSTs to
 * /brand-bible/ai-suggest with `tenant_id` required, no canned fallback, and
 * a body the route actually accepts: `section` + `section_id` + `targets` +
 * `tenant_context` + `section_context`. The old path posted a flat
 * {prompt, kind, existing, dismissed} body via aiSuggestService, which the
 * route rejects. Server-side the response maps each output_key to its
 * target_field; routing uses THAT mapping, never a parse of chip text.
 *
 * COVERAGE (7 editable fields, ceiling of 3 buttons). A button "covers" a field
 * either by suggesting for it OR by owning an honest no-suggestion state for it:
 *   Button 1 · guardrails-banned-claims   → banned_claim, regulated            (both suggested, creative)
 *   Button 2 · guardrails-exclusions      → excluded_service (grounded; refuse
 *                                            when no stored service/business context),
 *                                            excluded_area (refused — grounded, no geo)
 *   Button 3 · guardrails-topics-platform → platform_note, applies_to (suggested),
 *                                            sensitive_topic (refused — grounded, no history)
 *
 * excluded_service MUST carry bounded stored services / business type in
 * tenant_context/section_context. With no such context the target is sent
 * grounded and treated as a refusal — the model is not asked to invent
 * offerings. excluded_area and sensitive_topic never receive guessed
 * geography or history; they stay grounded and context-free.
 *
 * PER-FIELD MAPPING: each draft's output_key + target_field must match a
 * declared suggestable field or it is DROPPED. onAccept looks the chip label
 * up and dispatches to THAT field's own guarded action — it never chooses a
 * kind itself. applies_to writes the note-scope input, not a rule row.
 * Accept never calls verifyEntry.
 *
 * INDUSTRY NEUTRALITY: instructions, placeholders, labels, refusal copy and
 * provenance name no trade, regulator, standard, region, price, date, credential,
 * URL, or worked example.
 */
import { useRef, useState } from 'react';
import { AISuggest } from './AISuggest';
import {
  isValidUnverifiedDraft,
  requestModelFieldSuggestions,
  type ContextItem,
  type FieldRefusal,
  type TargetFieldMapping,
} from '@/services/brandBibleAiSuggest';
import { useBrandBibleWizardStore, selectEntries } from '@/stores/brandBibleWizardStore';
import type { ComplianceKind } from '@/features/brand-bible-wizard/types';

// Non-committal provenance shown before a fetch resolves; claims no grounding.
// Replaced by the server envelope's own `source` once a response lands.
const SUGGEST_SOURCE = 'unverified — review each';
const MISS_MSG =
  "One suggestion couldn't be matched to a field, so it was dropped — nothing was written. Try the button again.";

// The two 502 events the shared route distinguishes. Exported so tests pin the
// exact rendered string rather than a paraphrase.
export const PROVIDER_ERROR_MSG =
  "Suggestions couldn't be generated — the AI service didn't respond. Nothing was changed; try again.";
export const OUTPUT_ERROR_MSG =
  "The AI returned a response that didn't fit the expected shape, so it was discarded — nothing was written. Try again.";

// Honest no-suggestion copy for fields a model cannot know without facts.
// Exported so tests pin the exact rendered string rather than a paraphrase.
export const EXCLUDED_AREA_NO_SUGGEST =
  "Excluded areas aren't suggested — they depend on this client's own service geography. Enter them yourself.";
export const SENSITIVE_TOPIC_NO_SUGGEST =
  "Sensitive topics aren't suggested — they depend on this client's own history. Enter them yourself.";
export const EXCLUDED_SERVICE_NO_CONTEXT =
  "Excluded services aren't suggested without this client's recorded services or business type. Enter them yourself.";

const SECTION_CODE = 'G';
const CHIP_MAX_DRAFTS = 5;
const CONTEXT_VALUE_MAX = 1500;

/** One field a suggest button suggests for. `id` is BOTH the React key and the
 *  contract output_key/target_field (unique per button). `accept` is the field's
 *  guarded action — rule fields call the panel's addRule; the applies_to field
 *  fills the note scope input. The button itself never picks a kind. */
export interface SuggestField {
  id: string;
  /** Chip prefix — makes the target field visible before the operator accepts. */
  label: string;
  /** Neutral, generic, <=400-char instruction sent as the target's instruction. */
  instruction: string;
  /** Recorded values for this field, excluded from results so they aren't re-suggested. */
  existing: string[];
  accept: (text: string) => void;
  claimPolicy?: 'creative' | 'grounded';
}

/** A field the button covers but deliberately never suggests for. It is declared
 *  to the route as a grounded target (server refuses every draft without
 *  supporting context) AND has no chip-producing field client-side, so no
 *  machine value can reach it. `note` is the one honest line shown. */
export interface RefusedField {
  id: string;
  instruction: string;
  note: string;
}

export interface SuggestTarget {
  fieldId: string;
  text: string;
}

type ErrorKind = 'provider' | 'output' | 'adapter' | null;

/** Exact-match an accepted chip label back to its field + clean text. Returns
 *  null on a miss so the caller drops it — NEVER guesses a kind. Pure + exported
 *  for direct testing. */
export function resolveSuggestionTarget(
  map: ReadonlyMap<string, SuggestTarget>,
  label: string,
): SuggestTarget | null {
  return map.get(label) ?? null;
}

const norm = (s: string) => s.trim().toLowerCase();

// TargetFieldMapping.existing/dismissed are ShortText (<=400 chars) lists of <=20.
const bound = (xs: string[]): string[] =>
  xs.map((x) => x.trim()).filter(Boolean).map((x) => x.slice(0, 400)).slice(0, 20);

export function contextItem(key: string, value: string | undefined | null): ContextItem | null {
  const v = (value ?? '').replace(/\s+/g, ' ').trim().slice(0, CONTEXT_VALUE_MAX);
  if (!v) return null;
  return { key, value: v };
}

// Both refusing 502s arrive as the same status; the route's `detail` copy is the
// only signal. Adapter-thrown Errors (missing tenant, missing drafts/refusals)
// have no HTTP envelope.
function classifyError(err: unknown): { kind: Exclude<ErrorKind, null>; detail?: string } {
  const axiosDetail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  const message = err instanceof Error ? err.message : '';
  const detail = String(axiosDetail ?? message);
  const lower = detail.toLowerCase();
  if (axiosDetail != null) {
    return { kind: lower.includes('unmapped') ? 'output' : 'provider' };
  }
  if (lower.includes('unmapped') || lower.includes('missing drafts') || lower.includes('invalid')) {
    return { kind: 'output' };
  }
  return { kind: 'adapter', detail: detail.trim().slice(0, 400) || undefined };
}

function toTarget(
  outputKey: string,
  instruction: string,
  claimPolicy: 'creative' | 'grounded',
  existing: string[],
  dismissed: string[],
  maxDrafts: number,
): TargetFieldMapping {
  return {
    output_key: outputKey,
    target_field: outputKey,
    instruction,
    kind: 'chips',
    max_drafts: maxDrafts,
    claim_policy: claimPolicy,
    existing: bound(existing),
    dismissed: bound(dismissed),
  };
}

/** One suggest pill covering N §G fields (see file header). `refused` are the
 *  fields this button covers but never suggests for — declared grounded to the
 *  route and shown as one honest line each so coverage stays honest without
 *  inventing a value. */
export function ComplianceSuggest({
  sectionId,
  placeholder,
  fields,
  refused = [],
  tenantContext = [],
  sectionContext = [],
}: {
  sectionId: string;
  placeholder: string;
  fields: SuggestField[];
  refused?: RefusedField[];
  tenantContext?: ContextItem[];
  sectionContext?: ContextItem[];
}) {
  const tenantId = useBrandBibleWizardStore((s) => s.tenantId);
  const targetMap = useRef<Map<string, SuggestTarget>>(new Map());
  const [miss, setMiss] = useState(false);
  const [errorKind, setErrorKind] = useState<ErrorKind>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [serverRefusals, setServerRefusals] = useState<FieldRefusal[]>([]);
  const [displaySource, setDisplaySource] = useState(SUGGEST_SOURCE);

  const fetcher = async ({ dismissed }: { existing: string[]; dismissed: string[] }) => {
    setMiss(false);
    setErrorKind(null);
    setErrorDetail(null);
    setServerRefusals([]);

    const targets: TargetFieldMapping[] = [
      ...fields.map((field) =>
        toTarget(
          field.id,
          field.instruction,
          field.claimPolicy ?? 'creative',
          field.existing,
          dismissed,
          CHIP_MAX_DRAFTS,
        ),
      ),
      ...refused.map((field) => toTarget(field.id, field.instruction, 'grounded', [], [], 1)),
    ];

    let data;
    try {
      data = await requestModelFieldSuggestions(
        {
          section: SECTION_CODE,
          section_id: sectionId,
          targets,
          tenant_context: tenantContext,
          section_context: sectionContext,
        },
        tenantId,
      );
    } catch (err) {
      const classified = classifyError(err);
      setErrorKind(classified.kind);
      setErrorDetail(classified.detail ?? null);
      throw err;
    }

    const source = data.source?.trim() || SUGGEST_SOURCE;
    setDisplaySource(source);

    const refusedIds = new Set(refused.map((f) => f.id));
    const suggestableRefusals = (data.refusals ?? []).filter(
      (r) => !refusedIds.has(r.output_key) && fields.some((f) => f.id === r.output_key),
    );
    setServerRefusals(suggestableRefusals);

    const byId = new Map(fields.map((f) => [f.id, f] as const));
    const map = new Map<string, SuggestTarget>();
    const items: string[] = [];
    for (const draft of data.drafts ?? []) {
      if (!isValidUnverifiedDraft(draft)) continue;
      const field = byId.get(draft.output_key);
      if (!field || draft.target_field !== field.id) continue;
      const text = draft.value.trim();
      if (!text) continue;
      if (field.existing.some((e) => norm(e) === text.toLowerCase())) continue;
      const label = `${field.label} — ${text}`;
      if (map.has(label)) continue;
      map.set(label, { fieldId: field.id, text });
      items.push(label);
    }
    targetMap.current = map;
    return { items, source };
  };

  const onAccept = (label: string) => {
    const target = resolveSuggestionTarget(targetMap.current, label);
    if (!target) {
      setMiss(true);
      return;
    }
    setMiss(false);
    fields.find((f) => f.id === target.fieldId)?.accept(target.text);
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <AISuggest
        sectionId={sectionId}
        kind="chips"
        placeholder={placeholder}
        source={displaySource}
        existing={[]}
        fetcher={fetcher}
        onAccept={onAccept}
      />
      {refused.map((field) => (
        <p key={field.id} className="text-right text-[11px] text-muted-foreground">
          {field.note}
        </p>
      ))}
      {serverRefusals.map((r) => (
        <p key={`refusal-${r.output_key}`} className="text-right text-[11px] text-muted-foreground">
          {r.reason}
        </p>
      ))}
      {errorKind && (
        <p role="alert" className="text-right text-[11px] text-destructive">
          {errorKind === 'output'
            ? OUTPUT_ERROR_MSG
            : errorKind === 'adapter' && errorDetail
              ? errorDetail
              : PROVIDER_ERROR_MSG}
        </p>
      )}
      {miss && (
        <p role="alert" className="text-[11px] text-destructive">
          {MISS_MSG}
        </p>
      )}
    </div>
  );
}

/* ------------------------------- Button configs ------------------------------ */

export function BannedClaimsSuggest({
  bannedExisting,
  regulatedExisting,
  onAcceptRule,
}: {
  bannedExisting: string[];
  regulatedExisting: string[];
  onAcceptRule: (kind: ComplianceKind, text: string) => void;
}) {
  const fields: SuggestField[] = [
    {
      id: 'banned_claim',
      label: 'Banned claim',
      existing: bannedExisting,
      accept: (t) => onAcceptRule('banned_claim', t),
      instruction:
        'Propose short, generic categories of marketing claim that would need substantiation or third-party proof before publishing. Return brief category labels the operator can confirm or reject. No figures, names, regulators, or specifics.',
    },
    {
      id: 'regulated',
      label: 'Regulated',
      existing: regulatedExisting,
      accept: (t) => onAcceptRule('regulated', t),
      instruction:
        'Propose short, generic categories of statement a credential-, licence-, or approval-tied rule would govern for a business in this position. Return brief category labels only. No specific regulator, number, or standard.',
    },
  ];
  return <ComplianceSuggest sectionId="guardrails-banned-claims" placeholder="Suggest claim categories" fields={fields} />;
}

export function buildExclusionContext(input: {
  businessName?: string | null;
  businessType?: string | null;
  offeredServices: string[];
}): { tenant_context: ContextItem[]; section_context: ContextItem[]; hasServiceContext: boolean } {
  const tenant_context = [contextItem('business_name', input.businessName)].filter(
    (item): item is ContextItem => item !== null,
  );
  const section_context = [
    contextItem('business_type', input.businessType),
    contextItem('offered_services', bound(input.offeredServices).join(', ')),
  ].filter((item): item is ContextItem => item !== null);
  return {
    tenant_context,
    section_context,
    hasServiceContext: tenant_context.length + section_context.length > 0,
  };
}

export function ExclusionsSuggest({
  serviceExisting,
  areaExisting,
  onAcceptRule,
}: {
  serviceExisting: string[];
  areaExisting: string[];
  onAcceptRule: (kind: ComplianceKind, text: string) => void;
}) {
  void areaExisting;
  const services = useBrandBibleWizardStore(selectEntries('services')).filter((e) => e.deleted_at === null);
  const identities = useBrandBibleWizardStore(selectEntries('identity')).filter((e) => e.deleted_at === null);
  const identity = identities[0];
  const { tenant_context, section_context, hasServiceContext } = buildExclusionContext({
    businessName: identity?.legal_name || identity?.dba,
    businessType: identity?.brand_overview,
    offeredServices: services.map((s) => s.name ?? ''),
  });

  const serviceInstruction = hasServiceContext
    ? 'Using only the supplied recorded services and business type, propose short generic service-type names commonly confused with this work so the operator can mark those not provided. Refuse if the context does not support a value. Brief labels only. No brands, prices, geography, or invented offerings.'
    : 'Do not propose any excluded service. The supplied context does not name this business\'s services or type; return a refusal.';

  const serviceField: SuggestField = {
    id: 'excluded_service',
    label: 'Excluded service',
    existing: serviceExisting,
    claimPolicy: 'grounded',
    accept: (t) => onAcceptRule('excluded_service', t),
    instruction: serviceInstruction,
  };
  const areaRefused: RefusedField = {
    id: 'excluded_area',
    note: EXCLUDED_AREA_NO_SUGGEST,
    instruction:
      'Do not assert any specific service area or geography. Propose a value only if the supplied context explicitly names one; otherwise return a refusal.',
  };
  const serviceRefused: RefusedField = {
    id: 'excluded_service',
    note: EXCLUDED_SERVICE_NO_CONTEXT,
    instruction: serviceInstruction,
  };

  return (
    <ComplianceSuggest
      sectionId="guardrails-exclusions"
      placeholder="Suggest exclusions"
      fields={hasServiceContext ? [serviceField] : []}
      refused={hasServiceContext ? [areaRefused] : [serviceRefused, areaRefused]}
      tenantContext={tenant_context}
      sectionContext={section_context}
    />
  );
}

export function TopicsPlatformSuggest({
  topicExisting,
  noteExisting,
  appliesToExisting,
  onAcceptRule,
  onAcceptAppliesTo,
}: {
  topicExisting: string[];
  noteExisting: string[];
  appliesToExisting: string[];
  onAcceptRule: (kind: ComplianceKind, text: string) => void;
  /** Writes the scope tag into the note-scope input — NOT a rule row. */
  onAcceptAppliesTo: (value: string) => void;
}) {
  void topicExisting;
  const fields: SuggestField[] = [
    {
      id: 'platform_note',
      label: 'Platform note',
      existing: noteExisting,
      accept: (t) => onAcceptRule('platform_note', t),
      instruction:
        'Propose short, generic categories of rule that apply on only one channel or content type rather than everywhere. Return brief labels the operator can confirm or reject. No specific platform names.',
    },
    {
      id: 'applies_to',
      label: 'Applies-to scope',
      existing: appliesToExisting,
      accept: (t) => onAcceptAppliesTo(t),
      instruction:
        'Propose short scope values, each naming a single channel or content type a rule might apply to — one or two words, never a sentence. Keep them generic and structural for the operator to confirm or replace.',
    },
  ];
  const refused: RefusedField[] = [
    {
      id: 'sensitive_topic',
      note: SENSITIVE_TOPIC_NO_SUGGEST,
      instruction:
        'Do not assert any specific history, incident, or event. Propose a value only if the supplied context explicitly names one; otherwise return a refusal.',
    },
  ];
  return (
    <ComplianceSuggest
      sectionId="guardrails-topics-platform"
      placeholder="Suggest topics & scopes"
      fields={fields}
      refused={refused}
    />
  );
}
